// src/components/shop/MenuScanner.js
// Upload a menu image → Claude vision extracts items → select & add to shop
import React, { useState, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Upload, AlertCircle, CheckCircle, X, ScanLine, Image as ImageIcon } from 'lucide-react';

// ─── Styled Components ────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.82);
  backdrop-filter: blur(10px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  animation: fadeIn 0.25s ease;
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
`;

const Modal = styled.div`
  background: ${p => p.theme?.colors?.surface || '#1a1a1a'};
  border: 2px solid ${p => p.theme?.colors?.accent || '#800000'};
  border-radius: 16px;
  padding: 1.75rem;
  width: 100%;
  max-width: 640px;
  max-height: 92vh;
  overflow-y: auto;
  box-shadow: 0 24px 64px rgba(0,0,0,0.6);
  animation: slideUp 0.25s ease;
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @media (max-width: 768px) {
    padding: 1.25rem;
    max-height: 90vh;
    border-radius: 12px;
  }
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: ${p => p.theme?.colors?.accent || '#800000'}60; border-radius: 2px; }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${p => `${p.theme?.colors?.accent || '#800000'}30`};
  h2 {
    margin: 0;
    color: ${p => p.theme?.colors?.accent || '#800000'};
    font-size: 1.3rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  p {
    margin: 0.25rem 0 0;
    font-size: 0.8rem;
    opacity: 0.5;
    color: ${p => p.theme?.colors?.text || '#fff'};
  }
`;

const CloseBtn = styled.button`
  background: transparent; border: none;
  color: ${p => p.theme?.colors?.text || '#fff'};
  cursor: pointer; padding: 0.4rem;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%;
  transition: background 0.15s;
  &:hover { background: rgba(255,255,255,0.1); }
  align-self: flex-start;
`;

const DropZone = styled.div`
  border: 2px dashed ${p => p.dragging
    ? (p.theme?.colors?.accent || '#800000')
    : 'rgba(255,255,255,0.18)'};
  border-radius: 12px;
  padding: 2rem 1rem;
  text-align: center;
  cursor: pointer;
  background: ${p => p.dragging ? `${p.theme?.colors?.accent || '#800000'}0f` : 'rgba(255,255,255,0.03)'};
  transition: border-color 0.15s, background 0.15s;
  margin-bottom: 1rem;
  &:hover {
    border-color: ${p => p.theme?.colors?.accent || '#800000'};
    background: ${p => `${p.theme?.colors?.accent || '#800000'}08`};
  }
  .icon { opacity: 0.35; margin-bottom: 0.6rem; }
  .label {
    font-size: 0.9rem;
    font-weight: 600;
    color: ${p => p.theme?.colors?.text || '#fff'};
    margin-bottom: 0.25rem;
  }
  .sub { font-size: 0.76rem; opacity: 0.4; color: ${p => p.theme?.colors?.text || '#fff'}; }
`;

const PreviewArea = styled.div`
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 1rem;
  background: #000;
  max-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    max-width: 100%;
    max-height: 280px;
    object-fit: contain;
    display: block;
  }
`;

const RemoveImgBtn = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: rgba(0,0,0,0.7);
  border: none;
  border-radius: 50%;
  width: 30px; height: 30px;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  color: #fff;
  &:hover { background: rgba(200,40,40,0.8); }
`;

const ScanBtn = styled.button`
  width: 100%;
  background: ${p => p.disabled
    ? 'rgba(255,255,255,0.08)'
    : (p.theme?.colors?.accentGradient || p.theme?.colors?.accent || '#800000')};
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 0.85rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  display: flex; align-items: center; justify-content: center; gap: 0.5rem;
  opacity: ${p => p.disabled ? 0.4 : 1};
  transition: opacity 0.15s;
  margin-bottom: 1rem;
  &:not(:disabled):hover { opacity: 0.88; }
`;

const StatusMsg = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  margin-bottom: 0.9rem;
  background: ${p =>
    p.type === 'error'   ? 'rgba(244,67,54,0.12)'  :
    p.type === 'success' ? 'rgba(76,175,80,0.12)'  :
    'rgba(33,150,243,0.10)'};
  border: 1px solid ${p =>
    p.type === 'error'   ? 'rgba(244,67,54,0.3)'  :
    p.type === 'success' ? 'rgba(76,175,80,0.3)'  :
    'rgba(33,150,243,0.25)'};
  color: ${p =>
    p.type === 'error'   ? '#ef5350' :
    p.type === 'success' ? '#66bb6a' :
    '#64b5f6'};
  svg { flex-shrink: 0; margin-top: 1px; }
`;

const SelectionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.6rem;
  font-size: 0.8rem;
  color: ${p => p.theme?.colors?.textMuted || 'rgba(255,255,255,0.5)'};
  button {
    background: transparent; border: none;
    color: ${p => p.theme?.colors?.accent || '#800000'};
    font-size: 0.8rem; cursor: pointer; padding: 0;
    &:hover { text-decoration: underline; }
  }
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  max-height: 320px;
  overflow-y: auto;
  margin-bottom: 1rem;
  padding-right: 4px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: ${p => p.theme?.colors?.accent || '#800000'}50; border-radius: 2px; }
`;

const ItemRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  cursor: pointer;
  background: ${p => p.checked ? `${p.theme?.colors?.accent || '#800000'}18` : 'rgba(255,255,255,0.04)'};
  border: 1px solid ${p => p.checked ? `${p.theme?.colors?.accent || '#800000'}50` : 'rgba(255,255,255,0.08)'};
  transition: background 0.12s, border-color 0.12s;
  &:hover { background: ${p => `${p.theme?.colors?.accent || '#800000'}10`}; }
`;

const CategoryTag = styled.span`
  font-size: 0.68rem;
  padding: 2px 7px;
  border-radius: 4px;
  background: ${p => `${p.theme?.colors?.accent || '#800000'}25`};
  color: ${p => p.theme?.colors?.accent || '#800000'};
  flex-shrink: 0;
  white-space: nowrap;
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
  .name {
    font-size: 0.88rem;
    font-weight: 600;
    color: ${p => p.theme?.colors?.text || '#fff'};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .meta {
    font-size: 0.76rem;
    color: ${p => p.theme?.colors?.textMuted || 'rgba(255,255,255,0.45)'};
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 16px; height: 16px;
  flex-shrink: 0; cursor: pointer;
  accent-color: ${p => p.theme?.colors?.accent || '#800000'};
`;

const AddBtn = styled.button`
  width: 100%;
  background: ${p => p.theme?.colors?.accentGradient || p.theme?.colors?.accent || '#800000'};
  color: #fff; border: none;
  border-radius: 10px; padding: 0.85rem;
  font-size: 0.95rem; font-weight: 600;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 0.5rem;
  transition: opacity 0.15s;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &:not(:disabled):hover { opacity: 0.88; }
`;

const Spinner = styled.div`
  width: 20px; height: 20px;
  border: 2px solid rgba(255,255,255,0.25);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 0.8s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const MenuScanner = ({ onClose, onItemsExtracted, theme }) => {
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [mediaType, setMediaType] = useState('image/jpeg');
  const [dragging, setDragging] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const fileInputRef = useRef(null);

  const loadImage = useCallback((file) => {
    if (!file || !file.type.startsWith('image/')) {
      setStatus({ type: 'error', message: 'Please upload an image file (JPG, PNG, WEBP, etc.)' });
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setStatus({ type: 'error', message: 'Image too large. Please use an image under 20 MB.' });
      return;
    }
    setMediaType(file.type || 'image/jpeg');
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setImagePreview(dataUrl);
      // Extract pure base64 (strip the data:image/...;base64, prefix)
      setImageBase64(dataUrl.split(',')[1]);
      setItems([]);
      setSelected(new Set());
      setStatus(null);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) loadImage(file);
  }, [loadImage]);

  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = () => setDragging(false);

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) loadImage(file);
    e.target.value = '';
  };

  const scanMenu = async () => {
    if (!imageBase64) return;
    setScanning(true);
    setStatus({ type: 'info', message: 'Reading menu with AI…' });
    setItems([]);
    setSelected(new Set());

    try {
      const res = await fetch('/api/compress-media?action=scan-menu', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageBase64, mediaType })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Scan failed');
      }

      const extracted = data.items || [];
      if (extracted.length === 0) {
        setStatus({ type: 'error', message: 'No menu items found. Try a clearer or better-lit photo.' });
        return;
      }

      setItems(extracted);
      setSelected(new Set(extracted.map((_, i) => i)));
      setStatus({ type: 'success', message: `Found ${extracted.length} menu item${extracted.length !== 1 ? 's' : ''}! Select what you want to add.` });
    } catch (err) {
      setStatus({ type: 'error', message: err.message || 'Scan failed. Please try again.' });
    } finally {
      setScanning(false);
    }
  };

  const toggleAll = (select) =>
    setSelected(select ? new Set(items.map((_, i) => i)) : new Set());

  const toggleItem = (idx) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const handleAdd = () => {
    const chosen = items
      .filter((_, i) => selected.has(i))
      .map(item => ({
        name: item.name,
        price: item.price || '',
        description: item.description || '',
        category: item.category || 'Other',
        images: [null, null, null],
        currentImageIndex: 0,
        quantity: 1,
        address: '',
        coordinates: null
      }));
    if (chosen.length > 0) {
      onItemsExtracted(chosen);
      onClose();
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal theme={theme} onClick={e => e.stopPropagation()}>
        <Header theme={theme}>
          <div>
            <h2><ScanLine size={22} /> Scan Menu Image</h2>
            <p>Upload a photo of a menu — AI will extract all items automatically</p>
          </div>
          <CloseBtn theme={theme} onClick={onClose}><X size={20} /></CloseBtn>
        </Header>

        {/* Drop zone / image preview */}
        {!imagePreview ? (
          <DropZone
            theme={theme}
            dragging={dragging}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="icon"><Upload size={36} /></div>
            <div className="label">Drop a menu image here, or click to browse</div>
            <div className="sub">JPG, PNG, WEBP — max 20 MB</div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileInput}
            />
          </DropZone>
        ) : (
          <PreviewArea>
            <img src={imagePreview} alt="Menu preview" />
            <RemoveImgBtn onClick={() => { setImagePreview(null); setImageBase64(null); setItems([]); setStatus(null); }}>
              <X size={14} />
            </RemoveImgBtn>
          </PreviewArea>
        )}

        {/* Scan button */}
        {imagePreview && (
          <ScanBtn theme={theme} onClick={scanMenu} disabled={scanning || !imageBase64}>
            {scanning ? <><Spinner /> Reading menu…</> : <><ScanLine size={18} /> Scan Menu</>}
          </ScanBtn>
        )}

        {/* Status */}
        {status && (
          <StatusMsg type={status.type}>
            {status.type === 'error'   && <AlertCircle size={16} />}
            {status.type === 'success' && <CheckCircle size={16} />}
            {status.type === 'info'    && <Spinner style={{ width: 16, height: 16, border: '2px solid rgba(100,181,246,0.3)', borderTopColor: '#64b5f6' }} />}
            <span>{status.message}</span>
          </StatusMsg>
        )}

        {/* Item list */}
        {items.length > 0 && (
          <>
            <SelectionBar theme={theme}>
              <span>{selected.size} / {items.length} selected</span>
              <span>
                <button onClick={() => toggleAll(true)}>Select all</button>
                {' · '}
                <button onClick={() => toggleAll(false)}>Deselect all</button>
              </span>
            </SelectionBar>

            <ItemList theme={theme}>
              {items.map((item, idx) => (
                <ItemRow key={idx} theme={theme} checked={selected.has(idx)} onClick={() => toggleItem(idx)}>
                  <Checkbox
                    theme={theme}
                    checked={selected.has(idx)}
                    onChange={() => toggleItem(idx)}
                    onClick={e => e.stopPropagation()}
                  />
                  <ItemInfo theme={theme}>
                    <div className="name">{item.name}</div>
                    <div className="meta">
                      {item.price ? `$${parseFloat(item.price || 0).toFixed(2)}` : 'No price listed'}
                      {item.description ? ` · ${item.description.slice(0, 70)}` : ''}
                    </div>
                  </ItemInfo>
                  {item.category && item.category !== 'Other' && (
                    <CategoryTag theme={theme}>{item.category}</CategoryTag>
                  )}
                </ItemRow>
              ))}
            </ItemList>

            <AddBtn theme={theme} onClick={handleAdd} disabled={selected.size === 0}>
              <CheckCircle size={18} />
              Add {selected.size} Item{selected.size !== 1 ? 's' : ''} to Shop
            </AddBtn>
          </>
        )}
      </Modal>
    </Overlay>
  );
};

export default MenuScanner;
